import React, { useEffect, useMemo, useState } from "react";
import { SortProvider, STh, useColumnSort } from "@/components/SortTh";
import { LoadingRow } from "@/components/LoadingState";
import {
  Building2, Plus, Edit, Trash2, Search, AlertTriangle, X,
  GitMerge, Eye, TrendingUp, EyeOff, FileText, Download,
  Star, StickyNote, Tag, ListFilter, GripVertical, SortDesc, Upload, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatTL, API, fmtDate } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const TAG_OPTIONS = [
  { value: "",            label: "— Etiket Yok —", cls: "" },
  { value: "vip",         label: "VIP",            cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { value: "long_term",   label: "Uzun Vadeli",    cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { value: "aggressive",  label: "Agresif Takip",  cls: "bg-rose-100 text-rose-800 border-rose-300" },
  { value: "new",         label: "Yeni",           cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { value: "watch",       label: "İzlemede",       cls: "bg-slate-100 text-slate-700 border-slate-300" },
];
const TAG_MAP = Object.fromEntries(TAG_OPTIONS.map((t) => [t.value, t]));

export function TagBadge({ tag }) {
  const t = TAG_MAP[tag];
  if (!t || !t.value) return null;
  return (
    <span
      data-testid={`creditor-tag-${t.value}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${t.cls}`}
    >
      <Tag size={9} /> {t.label}
    </span>
  );
}

export default function CreditorsTab() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [dupGroups, setDupGroups] = useState([]);
  const [flatDup, setFlatDup] = useState({});
  const [ignoredOpen, setIgnoredOpen] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState([]); // ORDERED (global)
  const [favoriteUsage, setFavoriteUsage] = useState({});
  const [favoriteRows, setFavoriteRows] = useState([]); // ordered creditor docs
  const [suggestions, setSuggestions] = useState([]); // top non-fav creditors (admin only)
  const [importOpen, setImportOpen] = useState(false);
  const MAX_FAVORITES = 3;

  const favoriteIdsSet = useMemo(() => new Set(favoriteIds), [favoriteIds]);

  const loadFavorites = async () => {
    try {
      const d = await api.get("/me/favorite-creditors");
      const rowsFav = d.data?.rows || [];
      setFavoriteRows(rowsFav);
      setFavoriteIds(rowsFav.map((x) => x.id));
      setFavoriteUsage(d.data?.usage || {});
    } catch {
      setFavoriteRows([]);
      setFavoriteIds([]);
      setFavoriteUsage({});
    }
    // Suggestions (admin only — ignores 403 silently)
    if (isAdmin) {
      try {
        const s = await api.get("/me/favorite-creditors/suggestions?limit=3");
        setSuggestions(s.data?.rows || []);
      } catch {
        setSuggestions([]);
      }
    } else {
      setSuggestions([]);
    }
  };

  const addSuggestedFavorite = async (sug) => {
    try {
      await api.post("/me/favorite-creditors", { creditor_id: sug.id });
      toast.success(`"${sug.ad}" favorilere eklendi`);
      await loadFavorites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Favoriye eklenemedi");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/creditors?with_usage=true");
      setRows(r.data?.rows || []);
      api.get("/creditors/duplicates")
        .then((d) => { setDupGroups(d.data?.groups || []); setFlatDup(d.data?.flat || {}); })
        .catch(() => {});
      loadFavorites();
    } catch (e) {
      toast.error("Müvekkil listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const toggleFavorite = async (row) => {
    if (!isAdmin) {
      toast.error("Favoriler globaldır — yalnızca admin değiştirebilir");
      return;
    }
    const isFav = favoriteIdsSet.has(row.id);
    try {
      if (isFav) {
        await api.delete(`/me/favorite-creditors/${row.id}`);
        toast.success("Favorilerden çıkarıldı");
      } else {
        await api.post("/me/favorite-creditors", { creditor_id: row.id });
        toast.success("Favorilere eklendi · Prim modalında chip olarak çıkacak");
      }
      await loadFavorites();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLocaleLowerCase("tr");
    if (!needle) return rows;
    return rows.filter((r) => (r.ad || "").toLocaleLowerCase("tr").includes(needle));
  }, [rows, q]);
  const credSort = useColumnSort(filtered);

  const totalUsage = useMemo(
    () => rows.reduce((acc, r) => acc + (r.usage_count || 0), 0),
    [rows],
  );

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectDupGroup = (groupId) => {
    const g = dupGroups.find((x) => x.group_id === groupId);
    if (!g) return;
    setSelectedIds(new Set(g.members.map((m) => m.id)));
    setMergeOpen(true);
  };

  const ignoreDupGroup = async (g) => {
    if (!window.confirm(`"${g.members.map((m) => m.ad).join(" ≈ ")}" grubu mükerrer olarak önerilmesin. Emin misiniz?`)) return;
    try {
      await api.post("/creditors/duplicates/ignore", {
        creditor_ids: g.members.map((m) => m.id),
      });
      toast.success("Grup yoksayıldı · artık mükerrer olarak önerilmeyecek");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Yoksayma başarısız");
    }
  };

  const handleDelete = async (row, force = false) => {
    try {
      await api.delete(`/creditors/${row.id}${force ? "?force=true" : ""}`);
      toast.success(force ? "Müvekkil silindi (zorla)" : "Müvekkil silindi");
      setConfirm(null);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail && typeof detail === "object") {
        setConfirm({ row, usage_count: detail.usage_count || 0, message: detail.message });
      } else {
        toast.error(typeof detail === "string" ? detail : "Silinemedi");
      }
    }
  };

  const selectedRows = rows.filter((r) => selectedIds.has(r.id));

  return (
    <div className="space-y-4" data-testid="creditors-tab">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white grid place-items-center">
            <Building2 size={18} />
          </div>
          <div>
            <div className="font-display text-lg font-extrabold text-slate-900 leading-tight">
              Müvekkiller
            </div>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider">
              Kayıtlı: {rows.length} · Toplam Prim Kullanımı: {totalUsage}
              {selectedIds.size > 0 && (
                <span className="ml-2 text-indigo-600">· Seçili: {selectedIds.size}</span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {selectedIds.size >= 2 && (
            <button
              data-testid="creditor-merge-button"
              onClick={() => setMergeOpen(true)}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
            >
              <GitMerge size={16} /> Birleştir ({selectedIds.size})
            </button>
          )}
          <button
            data-testid="creditor-ignored-button"
            onClick={() => setIgnoredOpen(true)}
            className="flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg font-semibold text-xs transition-colors"
            title="Yoksayılan mükerrer grupları yönet"
          >
            <ListFilter size={13} /> Yoksayılan Gruplar
          </button>
          {isAdmin && (
            <button
              data-testid="creditor-import-button"
              onClick={() => setImportOpen(true)}
              className="flex items-center gap-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-200 px-3 py-2 rounded-lg font-semibold text-xs transition-colors"
              title=".xlsx veya .csv'den müvekkil ve favori toplu içe aktar"
            >
              <Upload size={13} /> İçe Aktar
            </button>
          )}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="creditor-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Ara..."
              className="pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm w-56 focus:outline-none focus:ring-2 focus:ring-slate-900"
            />
          </div>
          <button
            data-testid="creditor-add-button"
            onClick={() => setEditing({ __new: true, ad: "" })}
            className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
          >
            <Plus size={16} /> Yeni Müvekkil
          </button>
        </div>
      </div>

      {dupGroups.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 flex flex-wrap items-center gap-2" data-testid="creditor-duplicates-banner">
          <AlertTriangle size={14} />
          <span className="font-semibold">
            {dupGroups.length} muhtemel mükerrer grup bulundu — tıklayarak birleştirin:
          </span>
          {dupGroups.map((g) => (
            <div key={g.group_id} className="inline-flex items-center bg-white border border-rose-300 rounded-md overflow-hidden">
              <button
                data-testid={`creditor-dup-group-${g.group_id}`}
                onClick={() => selectDupGroup(g.group_id)}
                className="inline-flex items-center gap-1 text-rose-700 hover:bg-rose-50 px-2 py-0.5 font-semibold"
                title="Bu grubu seçerek birleştir"
              >
                <GitMerge size={10} />
                {g.members.map((m) => m.ad).join(" ≈ ")}
              </button>
              <button
                data-testid={`creditor-dup-ignore-${g.group_id}`}
                onClick={() => ignoreDupGroup(g)}
                className="inline-flex items-center gap-1 border-l border-rose-300 text-slate-500 hover:bg-slate-50 hover:text-slate-700 px-1.5 py-0.5"
                title="Bu grup mükerrer değil, bir daha önerilmesin"
              >
                <EyeOff size={10} />
              </button>
            </div>
          ))}
        </div>
      )}

      {selectedIds.size >= 2 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-center gap-2">
          <GitMerge size={14} />
          <span>
            <strong>{selectedIds.size}</strong> müvekkil seçildi. Yukarıdaki <strong>Birleştir</strong> butonu ile bu kayıtları tek bir müvekkilde birleştirebilirsiniz.
            Prim kayıtları hedef müvekkile otomatik taşınır.
          </span>
          <button
            onClick={clearSelection}
            className="ml-auto text-amber-900 hover:underline font-semibold"
            data-testid="creditor-clear-selection"
          >
            Seçimi Temizle
          </button>
        </div>
      )}

      {/* Favoriler Yönetimi (Global, Admin-managed) */}
      {(favoriteRows.length > 0 || (isAdmin && suggestions.length > 0)) && (
        <FavoritesManager
          rows={favoriteRows}
          usage={favoriteUsage}
          isAdmin={isAdmin}
          onReload={loadFavorites}
          max={MAX_FAVORITES}
          suggestions={suggestions}
          onAddSuggestion={addSuggestedFavorite}
        />
      )}

      {importOpen && (
        <CreditorImportModal
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); }}
        />
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm sticky-table">
        <SortProvider value={credSort}>
        <table className="w-full text-sm" data-testid="creditors-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3 w-10"></th>
              <STh k="ad" className="px-4 py-3">Müvekkil Adı</STh>
              <STh k="usage_count" className="px-4 py-3 text-right">Prim Kayıt Sayısı</STh>
              <STh k="created_at" className="px-4 py-3">Oluşturulma</STh>
              <th className="px-4 py-3 text-right">İşlemler</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <LoadingRow colSpan="5" text="Müvekkiller yükleniyor..." />
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-400">
                {q ? "Arama kriterine uyan müvekkil yok" : "Henüz müvekkil kaydı yok. Yukarıdan ekleyin."}
              </td></tr>
            )}
            {credSort.sorted.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`creditor-row-${r.id}`}>
                <td className="px-4 py-3">
                  <input
                    data-testid={`creditor-check-${r.id}`}
                    type="checkbox"
                    checked={selectedIds.has(r.id)}
                    onChange={() => toggleSelect(r.id)}
                    className="w-4 h-4 accent-indigo-600 cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3">
                  <button
                    data-testid={`creditor-detail-${r.id}`}
                    onClick={() => setDetailId(r.id)}
                    className="font-semibold text-slate-900 hover:text-indigo-600 hover:underline text-left inline-flex items-center gap-1.5"
                    title="Prim detaylarını gör"
                  >
                    {r.ad}
                    <Eye size={12} className="opacity-50" />
                  </button>
                  <TagBadge tag={r.tag} />
                  {r.notes && (
                    <span
                      data-testid={`creditor-note-icon-${r.id}`}
                      className="ml-1.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 border border-slate-200 text-slate-500"
                      title={r.notes}
                    >
                      <StickyNote size={10} />
                    </span>
                  )}
                  {flatDup[r.id] && (
                    <button
                      data-testid={`creditor-dup-badge-${r.id}`}
                      onClick={() => selectDupGroup(flatDup[r.id])}
                      className="ml-2 inline-flex items-center gap-1 bg-rose-100 border border-rose-300 text-rose-700 hover:bg-rose-200 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"
                      title="Mükerrer olabilir · Birleştirmek ister misin?"
                    >
                      <AlertTriangle size={9} /> Mükerrer?
                    </button>
                  )}
                </td>
                <td className="px-4 py-3 text-right font-mono-tab">
                  <span
                    data-testid={`creditor-usage-${r.id}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                      (r.usage_count || 0) > 0
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : "bg-slate-100 text-slate-500 border border-slate-200"
                    }`}
                  >
                    {r.usage_count || 0}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">
                  {r.created_at ? fmtDate(String(r.created_at)) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex gap-1 items-center">
                    <button
                      data-testid={`creditor-favorite-${r.id}`}
                      onClick={() => toggleFavorite(r)}
                      disabled={!isAdmin}
                      className={`w-7 h-7 rounded grid place-items-center transition-colors ${
                        favoriteIdsSet.has(r.id)
                          ? "bg-amber-100 text-amber-600 hover:bg-amber-200"
                          : "text-slate-300 hover:bg-slate-100 hover:text-amber-500"
                      } ${!isAdmin ? "opacity-40 cursor-not-allowed" : ""}`}
                      title={
                        !isAdmin
                          ? "Favoriler globaldır — admin değiştirebilir"
                          : favoriteIdsSet.has(r.id)
                            ? "Favoriden çıkar"
                            : `Favorilere ekle (max ${MAX_FAVORITES})`
                      }
                    >
                      <Star size={14} className={favoriteIdsSet.has(r.id) ? "fill-amber-500" : ""} />
                    </button>
                    <button
                      data-testid={`creditor-edit-${r.id}`}
                      onClick={() => setEditing(r)}
                      className="inline-flex items-center gap-1 bg-slate-100 hover:bg-slate-200 text-slate-700 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                    >
                      <Edit size={12} /> Düzenle
                    </button>
                    <button
                      data-testid={`creditor-delete-${r.id}`}
                      onClick={() => handleDelete(r, false)}
                      className="inline-flex items-center gap-1 bg-rose-50 hover:bg-rose-100 text-rose-700 px-2.5 py-1.5 rounded-md text-xs font-semibold"
                    >
                      <Trash2 size={12} /> Sil
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </SortProvider>
      </div>

      {editing && (
        <EditModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {confirm && (
        <ForceDeleteModal
          info={confirm}
          onClose={() => setConfirm(null)}
          onForce={() => handleDelete(confirm.row, true)}
        />
      )}

      {mergeOpen && (
        <MergeModal
          candidates={selectedRows}
          onClose={() => setMergeOpen(false)}
          onDone={() => { setMergeOpen(false); clearSelection(); load(); }}
        />
      )}

      {detailId && (
        <CreditorDetailModal
          creditorId={detailId}
          onClose={() => setDetailId(null)}
        />
      )}

      {ignoredOpen && (
        <IgnoredGroupsModal
          onClose={() => setIgnoredOpen(false)}
          onChanged={load}
        />
      )}
    </div>
  );
}

function EditModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.__new;
  const [ad, setAd] = useState(initial.ad || "");
  const [notes, setNotes] = useState(initial.notes || "");
  const [tag, setTag] = useState(initial.tag || "");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const name = (ad || "").trim();
    if (!name) { toast.error("Müvekkil adı boş olamaz"); return; }
    setSaving(true);
    try {
      const body = { ad: name, notes: notes.slice(0, 1000), tag };
      if (isNew) {
        await api.post("/creditors", body);
        toast.success("Müvekkil eklendi");
      } else {
        await api.put(`/creditors/${initial.id}`, body);
        toast.success("Müvekkil güncellendi");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md my-8" data-testid="creditor-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Building2 size={18} /> {isNew ? "Yeni Müvekkil" : "Müvekkil Düzenle"}
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">
              Müvekkil Adı *
            </label>
            <input
              data-testid="creditor-input-ad"
              autoFocus
              value={ad}
              onChange={(e) => setAd(e.target.value)}
              placeholder="Örn: ABC Varlık Yönetim A.Ş."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              required
            />
            {!isNew && (
              <p className="text-[11px] text-slate-500 mt-1.5">
                Ad değiştirilirse, bu müvekkile bağlı tüm prim kayıtlarındaki ad da güncellenir.
              </p>
            )}
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 block mb-1.5">Etiket</label>
            <div className="flex flex-wrap gap-1.5" data-testid="creditor-tag-picker">
              {TAG_OPTIONS.map((t) => {
                const active = tag === t.value;
                return (
                  <button
                    key={t.value || "none"}
                    type="button"
                    data-testid={`creditor-tag-option-${t.value || "none"}`}
                    onClick={() => setTag(t.value)}
                    className={`inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border transition-all ${
                      active
                        ? (t.cls || "bg-slate-900 text-white border-slate-900") + " ring-2 ring-offset-1 ring-slate-900"
                        : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                    }`}
                  >
                    {t.value ? <Tag size={9} /> : null} {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 flex items-center gap-1 mb-1.5">
              <StickyNote size={11} /> İç Not <span className="font-normal normal-case opacity-70">(sadece admin/muhasebeci görür)</span>
            </label>
            <textarea
              data-testid="creditor-input-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              maxLength={1000}
              placeholder="Örn: Ödeme vadesi kısa · İtiraz süreci açık"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 resize-none"
            />
            <div className="text-[10px] text-slate-400 text-right mt-0.5 font-mono-tab">{notes.length}/1000</div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">İptal</button>
            <button
              data-testid="creditor-save-button"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >
              {saving ? "Kaydediliyor..." : "Kaydet"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ForceDeleteModal({ info, onClose, onForce }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-md" data-testid="creditor-force-delete-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2 text-rose-700">
            <AlertTriangle size={18} /> Dikkat · Kullanımdaki Müvekkil
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm text-slate-700">
            <strong>{info.row.ad}</strong> adlı müvekkil{" "}
            <span className="font-mono-tab bg-rose-50 text-rose-700 border border-rose-200 rounded px-1.5">
              {info.usage_count}
            </span>{" "}
            prim kaydında kullanılıyor.
          </p>
          <p className="text-xs text-slate-500">
            Silerseniz prim kayıtlarındaki müvekkil adı metin olarak kalır fakat liste seçeneği olarak görünmez. Bu işlem geri alınamaz.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">Vazgeç</button>
            <button
              data-testid="creditor-force-delete-confirm"
              onClick={onForce}
              className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold"
            >
              Yine de Sil
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MergeModal({ candidates, onClose, onDone }) {
  const [targetId, setTargetId] = useState(candidates[0]?.id || "");
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const target = candidates.find((c) => c.id === targetId);
  const sources = candidates.filter((c) => c.id !== targetId);
  const totalMovingPrim = sources.reduce((a, s) => a + (s.usage_count || 0), 0);

  const doMerge = async () => {
    if (!targetId) { toast.error("Hedef müvekkili seçin"); return; }
    if (sources.length === 0) { toast.error("En az bir kaynak müvekkil seçin"); return; }
    setBusy(true);
    try {
      const r = await api.post("/creditors/merge", {
        target_id: targetId,
        source_ids: sources.map((s) => s.id),
      });
      toast.success(`Birleştirildi · ${r.data.moved} prim kaydı taşındı`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Birleştirme başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-lg my-8" data-testid="creditor-merge-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <GitMerge size={18} /> Müvekkilleri Birleştir
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>

        {!confirming ? (
          <div className="p-5 space-y-4">
            <p className="text-xs text-slate-600">
              Seçili {candidates.length} müvekkil arasından <strong>kalacak olanı</strong> (hedef) seçin.
              Diğerleri silinir ve bağlı prim kayıtları hedefe otomatik taşınır.
            </p>

            <div className="space-y-2" data-testid="creditor-merge-target-list">
              {candidates.map((c) => (
                <label
                  key={c.id}
                  className={`flex items-center gap-3 border rounded-lg p-3 cursor-pointer transition-colors ${
                    c.id === targetId ? "border-indigo-500 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  <input
                    data-testid={`creditor-merge-target-${c.id}`}
                    type="radio"
                    name="merge-target"
                    checked={c.id === targetId}
                    onChange={() => setTargetId(c.id)}
                    className="accent-indigo-600"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-sm text-slate-900">{c.ad}</div>
                    <div className="text-[11px] text-slate-500">
                      {c.usage_count || 0} prim kaydı
                    </div>
                  </div>
                  {c.id === targetId && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-2 py-0.5 rounded-full">
                      Hedef
                    </span>
                  )}
                </label>
              ))}
            </div>

            {sources.length > 0 && target && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
                <div className="font-semibold mb-1 flex items-center gap-1.5">
                  <AlertTriangle size={12} /> Önizleme
                </div>
                <ul className="list-disc list-inside space-y-0.5">
                  {sources.map((s) => (
                    <li key={s.id}>
                      <strong>{s.ad}</strong> silinecek, {s.usage_count || 0} prim kaydı <strong>{target.ad}</strong>'a taşınacak
                    </li>
                  ))}
                  <li className="pt-1 font-bold">
                    Toplam {totalMovingPrim} prim kaydı taşınacak
                  </li>
                </ul>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">
                İptal
              </button>
              <button
                data-testid="creditor-merge-next"
                onClick={() => setConfirming(true)}
                disabled={!target || sources.length === 0}
                className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                Devam
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm">
              <div className="font-bold text-rose-800 flex items-center gap-2 mb-2">
                <AlertTriangle size={16} /> Birleştirmeyi Onayla
              </div>
              <p className="text-slate-700">
                <strong>{sources.length}</strong> müvekkil silinip, toplam <strong>{totalMovingPrim}</strong> prim kaydı{" "}
                <strong>{target?.ad}</strong>'a aktarılacak. <span className="text-rose-700">Bu işlem geri alınamaz.</span>
              </p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button onClick={() => setConfirming(false)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold">
                Geri
              </button>
              <button
                data-testid="creditor-merge-confirm"
                onClick={doMerge}
                disabled={busy}
                className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50"
              >
                {busy ? "Birleştiriliyor..." : "Evet, Birleştir"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CreditorDetailModal({ creditorId, onClose }) {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(new Date().getFullYear());
  const [isFav, setIsFav] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const MAX_FAV = 3;

  useEffect(() => {
    let alive = true;
    api.get(`/creditors/${creditorId}/prims`)
      .then((r) => { if (alive) setData(r.data); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Detay yüklenemedi"))
      .finally(() => alive && setLoading(false));
    api.get("/me/favorite-creditors")
      .then((r) => {
        if (!alive) return;
        const favs = r.data?.rows || [];
        setFavCount(favs.length);
        setIsFav(favs.some((f) => f.id === creditorId));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [creditorId]);

  const toggleFav = async () => {
    if (!isAdmin) {
      toast.error("Favoriler globaldır — yalnızca admin değiştirebilir");
      return;
    }
    try {
      if (isFav) {
        await api.delete(`/me/favorite-creditors/${creditorId}`);
        setIsFav(false);
        setFavCount((c) => Math.max(0, c - 1));
        toast.success("Favorilerden çıkarıldı");
      } else {
        await api.post("/me/favorite-creditors", { creditor_id: creditorId });
        setIsFav(true);
        setFavCount((c) => c + 1);
        toast.success("Favorilere eklendi · Prim modalında chip olarak çıkacak");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  const downloadYearlyPdf = async () => {
    try {
      const token = localStorage.getItem("bordromat_token") || "";
      const res = await fetch(`${API}/creditors/${creditorId}/yearly-pdf?yil=${year}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || "PDF oluşturulamadı");
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `muvekkil_${(data?.creditor?.ad || "rapor").replace(/\s+/g, "_")}_${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Yıllık PDF indirildi");
    } catch (e) {
      toast.error(e?.message || "PDF indirilemedi");
    }
  };

  const rows = data?.rows || [];
  const summary = data?.summary || { count: 0, total_tahsilat: 0, total_vekalet: 0, total_odenecek: 0 };
  const trend = data?.trend || [];
  const creditor = data?.creditor;
  const now = new Date().getFullYear();
  const years = [now, now - 1, now - 2];

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-4xl my-8" data-testid="creditor-detail-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="font-display text-lg font-bold flex items-center gap-2 flex-wrap">
              <Eye size={18} />
              <span className="truncate">{creditor?.ad || "Müvekkil Detay"}</span>
              <TagBadge tag={creditor?.tag} />
            </h3>
            <div className="text-[11px] text-slate-500 font-semibold uppercase tracking-wider mt-0.5">
              Prim Kayıt Geçmişi & Toplamlar
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="creditor-detail-favorite-toggle"
              onClick={toggleFav}
              disabled={!isAdmin}
              title={!isAdmin ? "Favoriler globaldır — admin değiştirebilir" : isFav ? "Favorilerden çıkar" : "Favorilere ekle (Prim modalında kısayol)"}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-bold border transition-colors ${
                isFav
                  ? "bg-amber-100 text-amber-800 border-amber-300"
                  : "bg-white text-slate-500 hover:bg-slate-50 border-slate-200"
              } ${!isAdmin ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Star size={13} className={isFav ? "fill-amber-500 text-amber-500" : ""} />
              {isFav ? "Favoride" : "Favori Yap"}
            </button>
            <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
          </div>
        </div>

        <div className="p-5 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="creditor-detail-summary">
            <SummaryCard label="Prim Kayıt Sayısı" value={summary.count} isCount tone="slate" />
            <SummaryCard label="Toplam Tahsilat" value={summary.total_tahsilat} tone="emerald" />
            <SummaryCard label="Toplam Vekalet" value={summary.total_vekalet} tone="indigo" />
            <SummaryCard label="Toplam Ödenecek" value={summary.total_odenecek} tone="amber" />
          </div>

          {/* Sparkline trend (last 6 months) */}
          {trend.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="creditor-detail-trend">
              <TrendCard
                label="Aylık Tahsilat (son 6 ay)"
                tone="emerald"
                data={trend.map((t) => ({ label: t.label, value: t.tahsilat }))}
                testid="trend-tahsilat"
              />
              <TrendCard
                label="Aylık Ödenecek Prim (son 6 ay)"
                tone="amber"
                data={trend.map((t) => ({ label: t.label, value: t.odenecek }))}
                testid="trend-odenecek"
              />
            </div>
          )}

          {/* Prim list */}
          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs" data-testid="creditor-detail-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2">Tarih</th>
                  <th className="px-3 py-2">İcra Föy</th>
                  <th className="px-3 py-2">Dosya</th>
                  <th className="px-3 py-2 text-right">Tahsilat</th>
                  <th className="px-3 py-2 text-right">Vekalet</th>
                  <th className="px-3 py-2 text-right">Ödenecek</th>
                  <th className="px-3 py-2">Giden Kişiler</th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan="7" className="px-3 py-6 text-center text-slate-400">Yükleniyor...</td></tr>
                )}
                {!loading && rows.length === 0 && (
                  <tr><td colSpan="7" className="px-3 py-6 text-center text-slate-400">Bu müvekkil için prim kaydı yok</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono-tab">{fmtDate(r.tarih)}</td>
                    <td className="px-3 py-2">{r.icra_foy || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{r.dosya_adi || "—"}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{formatTL(r.tahsilat_miktari || 0)}</td>
                    <td className="px-3 py-2 text-right font-mono-tab">{formatTL(r.vekalet_ucreti || 0)}</td>
                    <td className="px-3 py-2 text-right font-mono-tab font-semibold text-emerald-700">
                      {r.odenecek_toplam != null ? formatTL(r.odenecek_toplam) : "—"}
                    </td>
                    <td className="px-3 py-2 text-slate-600">
                      {(r.giden_isimleri || []).filter(Boolean).join(", ") || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2" data-testid="creditor-detail-yearly-pdf-group">
            <label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Yıllık PDF Rapor:</label>
            <select
              data-testid="creditor-detail-year-select"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              className="px-2 py-1 border border-slate-200 rounded-md text-xs font-semibold bg-white"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <button
              data-testid="creditor-detail-yearly-pdf-btn"
              onClick={downloadYearlyPdf}
              className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-md text-xs font-semibold"
            >
              <FileText size={13} /> Yıllık PDF İndir
            </button>
          </div>
          <button
            data-testid="creditor-detail-close"
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, tone = "slate", isCount = false }) {
  const toneCls =
    tone === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-200" :
    tone === "indigo" ? "bg-indigo-50 text-indigo-800 border-indigo-200" :
    tone === "amber" ? "bg-amber-50 text-amber-800 border-amber-200" :
    "bg-slate-50 text-slate-800 border-slate-200";
  return (
    <div className={`rounded-lg p-3 border ${toneCls}`}>
      <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div className="font-mono-tab font-bold text-lg mt-1">
        {isCount ? value : formatTL(value || 0)}
      </div>
    </div>
  );
}

function IgnoredGroupsModal({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/creditors/duplicates/ignored");
      setRows(r.data?.rows || []);
    } catch {
      toast.error("Liste yüklenemedi");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const unignore = async (signature) => {
    if (!window.confirm("Bu grup bir sonraki taramada tekrar mükerrer olarak önerilecek. Emin misiniz?")) return;
    try {
      await api.delete(`/creditors/duplicates/ignore?signature=${encodeURIComponent(signature)}`);
      toast.success("Yeniden gösterilecek");
      load();
      if (onChanged) onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl" data-testid="ignored-groups-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <EyeOff size={18} /> Yoksayılan Mükerrer Gruplar
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center"><X size={16} /></button>
        </div>
        <div className="p-5 max-h-[70vh] overflow-y-auto">
          {loading && <div className="text-center py-6 text-slate-400 text-sm">Yükleniyor...</div>}
          {!loading && rows.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm">
              Henüz yoksayılan grup yok. Müvekkiller listesinde mükerrer chip'inin yanındaki <EyeOff size={11} className="inline mx-0.5" /> ikonuyla gizleyebilirsiniz.
            </div>
          )}
          <ul className="space-y-2" data-testid="ignored-groups-list">
            {rows.map((r) => (
              <li
                key={r.signature}
                data-testid={`ignored-row-${r.signature}`}
                className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3 bg-slate-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-slate-900 truncate">
                    {(r.names || []).join(" ≈ ")}
                  </div>
                  <div className="text-[10px] text-slate-500 font-mono-tab mt-0.5">
                    Yoksayan: {r.created_by_email || "—"} · {fmtDate(r.created_at || "")}
                  </div>
                </div>
                <button
                  data-testid={`ignored-unignore-${r.signature}`}
                  onClick={() => unignore(r.signature)}
                  className="shrink-0 inline-flex items-center gap-1 bg-emerald-100 hover:bg-emerald-200 text-emerald-800 px-3 py-1.5 rounded-md text-xs font-semibold"
                >
                  Yeniden Göster
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

function TrendCard({ label, data, tone, testid }) {
  const [hoverIdx, setHoverIdx] = React.useState(null);
  const tones = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", stroke: "#059669", fill: "#D1FAE5" },
    amber: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", stroke: "#D97706", fill: "#FEF3C7" },
  };
  const t = tones[tone] || tones.emerald;
  const values = data.map((d) => d.value);
  const total = values.reduce((a, b) => a + b, 0);
  const max = Math.max(...values, 1);
  const last = values[values.length - 1] || 0;
  const prev = values[values.length - 2] || 0;
  const delta = last - prev;
  const deltaPct = prev > 0 ? ((last - prev) / prev) * 100 : (last > 0 ? 100 : 0);
  const isUp = delta > 0;

  // Build SVG path over 0..W × 0..H
  const W = 200, H = 36;
  const step = data.length > 1 ? W / (data.length - 1) : W;
  const pts = values.map((v, i) => {
    const x = i * step;
    const y = H - (v / max) * (H - 4) - 2;
    return [x, y];
  });
  const line = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = pts.length
    ? `${line} L${pts[pts.length - 1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z`
    : "";

  const hover = hoverIdx != null ? data[hoverIdx] : null;
  const hoverPt = hoverIdx != null ? pts[hoverIdx] : null;
  // Compute tooltip position within the SVG viewBox (percentages)
  const tipLeftPct = hoverPt ? Math.max(2, Math.min(98, (hoverPt[0] / W) * 100)) : 0;
  const tipTopPct = hoverPt ? Math.max(0, (hoverPt[1] / H) * 100) : 0;

  return (
    <div className={`rounded-lg p-3 border ${t.bg} ${t.border} ${t.text}`} data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
          <div className="font-mono-tab font-bold text-lg mt-0.5">{formatTL(total)}</div>
          <div className="text-[10px] font-semibold opacity-70">6 aylık toplam</div>
        </div>
        {total > 0 && (
          <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold ${isUp ? "bg-emerald-100 text-emerald-800" : delta < 0 ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>
            <TrendingUp size={10} className={isUp ? "" : "rotate-180"} />
            {deltaPct === 0 ? "±0%" : `${isUp ? "+" : ""}${deltaPct.toFixed(0)}%`}
          </div>
        )}
      </div>
      <div className="relative mt-1">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-10"
          preserveAspectRatio="none"
          onMouseLeave={() => setHoverIdx(null)}
          data-testid={`${testid}-svg`}
        >
          {area && <path d={area} fill={t.fill} />}
          {line && <path d={line} fill="none" stroke={t.stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />}
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={p[0]} cy={p[1]} r={hoverIdx === i ? 2.6 : 1.6} fill={t.stroke} />
              {/* Invisible wider hover target */}
              <rect
                x={Math.max(0, p[0] - step / 2)}
                y={0}
                width={step}
                height={H}
                fill="transparent"
                onMouseEnter={() => setHoverIdx(i)}
                data-testid={`${testid}-hit-${i}`}
                style={{ cursor: "pointer" }}
              />
            </g>
          ))}
        </svg>
        {hover && (
          <div
            className="absolute pointer-events-none bg-slate-900 text-white rounded-md shadow-lg px-2 py-1 text-[10px] font-semibold whitespace-nowrap z-10"
            style={{
              left: `${tipLeftPct}%`,
              top: `${tipTopPct}%`,
              transform: "translate(-50%, calc(-100% - 6px))",
            }}
            data-testid={`${testid}-tooltip`}
          >
            <div className="opacity-70 font-mono-tab">{hover.label}</div>
            <div className="font-mono-tab font-bold">{formatTL(hover.value)}</div>
          </div>
        )}
      </div>
      <div className="flex justify-between text-[9px] font-semibold opacity-70 mt-0.5 font-mono-tab">
        {data.map((d, i) => (
          <span key={i}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}


// =========================
// FavoritesManager — Global favoriler panel: drag-drop reorder + usage auto-sort
// Admin yazar; diğer roller sadece okur.
// =========================
function FavoritesManager({ rows, usage, isAdmin, onReload, max, suggestions, onAddSuggestion }) {
  const [order, setOrder] = useState(rows.map((r) => r.id));
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hoverId, setHoverId] = useState(null); // for notes/tag tooltip

  // Sync local order whenever parent rows change
  useEffect(() => {
    setOrder(rows.map((r) => r.id));
  }, [rows]);

  const byId = useMemo(
    () => Object.fromEntries(rows.map((r) => [r.id, r])),
    [rows],
  );
  const totalUsage = Object.values(usage || {}).reduce((a, b) => a + (b || 0), 0);
  const maxUsage = Math.max(1, ...Object.values(usage || {}));

  const persistOrder = async (next) => {
    try {
      setBusy(true);
      await api.put("/me/favorite-creditors/reorder", { creditor_ids: next });
      toast.success("Favori sıralaması kaydedildi");
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sıralama kaydedilemedi");
      setOrder(rows.map((r) => r.id)); // rollback
    } finally {
      setBusy(false);
    }
  };

  const handleDragStart = (id) => (e) => {
    if (!isAdmin) return;
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch { /* some browsers need this */ }
  };
  const handleDragOver = (id) => (e) => {
    if (!isAdmin || !dragId || dragId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverId(id);
  };
  const handleDrop = (targetId) => (e) => {
    e.preventDefault();
    if (!isAdmin || !dragId || dragId === targetId) {
      setDragId(null); setOverId(null); return;
    }
    const next = [...order];
    const from = next.indexOf(dragId);
    const to = next.indexOf(targetId);
    if (from === -1 || to === -1) {
      setDragId(null); setOverId(null); return;
    }
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setOrder(next);
    setDragId(null); setOverId(null);
    if (JSON.stringify(next) !== JSON.stringify(rows.map((r) => r.id))) {
      persistOrder(next);
    }
  };
  const handleDragEnd = () => { setDragId(null); setOverId(null); };

  const moveBy = (id, dir) => {
    if (!isAdmin) return;
    const next = [...order];
    const i = next.indexOf(id);
    if (i === -1) return;
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
    persistOrder(next);
  };

  const sortByUsage = async () => {
    if (!isAdmin) return;
    try {
      setBusy(true);
      const r = await api.post("/me/favorite-creditors/sort-by-usage");
      const nextOrder = r.data?.order || [];
      setOrder(nextOrder);
      toast.success("Favoriler kullanım sayısına göre sıralandı");
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Oto-sıralama başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-amber-50 border border-amber-200 rounded-xl p-4 shadow-sm"
      data-testid="favorites-manager"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2 min-w-0">
          <Star size={16} className="text-amber-600 fill-amber-500 shrink-0" />
          <div>
            <div className="font-display text-sm font-extrabold text-amber-900">
              Favori Müvekkiller (Global)
            </div>
            <div className="text-[10px] text-amber-800/70 font-semibold uppercase tracking-wider">
              {order.length}/{max} favori · Toplam kullanım: {totalUsage}
              {isAdmin ? " · Sürükle-bırak ile sırala" : " · Admin yönetir"}
            </div>
          </div>
        </div>
        {isAdmin && (
          <button
            data-testid="favorites-sort-by-usage"
            onClick={sortByUsage}
            disabled={busy || order.length < 2}
            className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50"
            title="En çok prim kaydı olan müvekkili başa al"
          >
            <SortDesc size={13} /> Kullanıma Göre Sırala
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2" data-testid="favorites-list">
        {order.length === 0 && (
          <div className="text-xs text-amber-800/70 italic py-2 px-1" data-testid="favorites-empty">
            Henüz favori eklenmemiş · aşağıdaki önerilerden hızlıca ekleyebilirsiniz
          </div>
        )}
        {order.map((id, idx) => {
          const r = byId[id];
          if (!r) return null;
          const cnt = usage?.[id] || 0;
          const pct = Math.round((cnt / maxUsage) * 100);
          const isDrag = dragId === id;
          const isOver = overId === id && dragId !== id;
          const notes = (r.notes || "").trim();
          const tagMeta = TAG_MAP[r.tag];
          const hasContext = !!(notes || (tagMeta && tagMeta.value));
          const isHover = hoverId === id;
          return (
            <div
              key={id}
              data-testid={`favorite-item-${id}`}
              draggable={isAdmin}
              onDragStart={handleDragStart(id)}
              onDragOver={handleDragOver(id)}
              onDrop={handleDrop(id)}
              onDragEnd={handleDragEnd}
              onMouseEnter={() => hasContext && setHoverId(id)}
              onMouseLeave={() => setHoverId(null)}
              className={`relative flex items-center gap-2 bg-white border rounded-lg px-3 py-2 shadow-sm transition-all ${
                isDrag ? "opacity-40 scale-95" : ""
              } ${isOver ? "border-amber-500 ring-2 ring-amber-300" : "border-amber-200"} ${
                isAdmin ? "cursor-move" : ""
              }`}
              style={{ minWidth: 220 }}
            >
              {isAdmin && (
                <span className="text-amber-500" title="Sürüklemek için tut">
                  <GripVertical size={14} />
                </span>
              )}
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-600 text-white text-[10px] font-bold font-mono-tab">
                {idx + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-bold text-slate-800 truncate flex items-center gap-1" title={r.ad}>
                  <span className="truncate">{r.ad}</span>
                  {notes && (
                    <StickyNote size={10} className="text-amber-600 shrink-0" aria-label="Not var" />
                  )}
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="flex-1 h-1.5 bg-amber-100 rounded-full overflow-hidden" title={`${cnt} prim kaydı`}>
                    <div className="h-full bg-amber-500" style={{ width: `${pct}%` }}></div>
                  </div>
                  <span className="text-[10px] font-bold text-amber-800 font-mono-tab">{cnt}</span>
                </div>
              </div>
              {isHover && hasContext && (
                <div
                  data-testid={`favorite-tooltip-${id}`}
                  className="absolute z-50 left-0 right-0 top-full mt-1.5 bg-slate-900 text-white rounded-lg shadow-xl p-2.5 text-[11px] pointer-events-none"
                  style={{ maxWidth: 320 }}
                >
                  <div className="font-bold text-amber-200 mb-1 flex items-center gap-1.5">
                    {r.ad}
                    {tagMeta && tagMeta.value && (
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider border ${tagMeta.cls}`}>
                        <Tag size={8} /> {tagMeta.label}
                      </span>
                    )}
                  </div>
                  {notes ? (
                    <div className="text-slate-200 whitespace-pre-wrap leading-snug">
                      {notes.length > 400 ? notes.slice(0, 400) + "…" : notes}
                    </div>
                  ) : (
                    <div className="text-slate-400 italic">Not yok</div>
                  )}
                  <div className="mt-1.5 text-[9px] text-slate-400 font-mono-tab">
                    {cnt} prim kaydı
                  </div>
                  <span
                    className="absolute -top-1 left-6 w-2 h-2 bg-slate-900 rotate-45"
                    aria-hidden
                  ></span>
                </div>
              )}
              {isAdmin && (
                <div className="flex flex-col gap-0.5">
                  <button
                    data-testid={`favorite-move-up-${id}`}
                    onClick={() => moveBy(id, -1)}
                    disabled={idx === 0 || busy}
                    className="w-5 h-4 text-[9px] font-bold leading-none text-amber-700 hover:bg-amber-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Yukarı taşı"
                  >▲</button>
                  <button
                    data-testid={`favorite-move-down-${id}`}
                    onClick={() => moveBy(id, +1)}
                    disabled={idx === order.length - 1 || busy}
                    className="w-5 h-4 text-[9px] font-bold leading-none text-amber-700 hover:bg-amber-100 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title="Aşağı taşı"
                  >▼</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {isAdmin && suggestions && suggestions.length > 0 && order.length < max && (
        <div className="mt-3 pt-3 border-t border-amber-200" data-testid="favorites-suggestions">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={12} className="text-amber-700" />
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800">
              Önerilen · En Çok Kullanılan (favoride olmayanlar)
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s.id}
                data-testid={`favorite-suggestion-${s.id}`}
                onClick={() => onAddSuggestion(s)}
                disabled={busy || order.length >= max}
                className="inline-flex items-center gap-1 bg-white hover:bg-amber-50 border border-amber-300 text-amber-900 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title={`${s.ad} · ${s.usage} prim kaydı · Favorilere ekle`}
              >
                <Plus size={11} className="text-amber-600" />
                <span className="truncate max-w-[180px]">{s.ad}</span>
                <span className="inline-flex items-center justify-center min-w-[20px] h-4 px-1 rounded-full bg-amber-200 text-amber-900 text-[9px] font-bold font-mono-tab">
                  {s.usage}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =========================
// CreditorImportModal — xlsx bulk import; favori column sets global favorites
// =========================
function CreditorImportModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async (fmt = "xlsx") => {
    try {
      const token = localStorage.getItem("bordromat_token") || "";
      const path = fmt === "csv" ? "import-template-csv" : "import-template";
      const res = await fetch(`${API}/creditors/${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text() || "Şablon indirilemedi");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `muvekkil_import_sablonu.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} şablonu indirildi`);
    } catch (e) {
      toast.error(e?.message || "Şablon indirilemedi");
    }
  };

  const doImport = async () => {
    if (!file) {
      toast.error("Lütfen bir .xlsx dosyası seçin");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/creditors/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      toast.success(`İçe aktarım tamamlandı · ${r.data?.created || 0} yeni, ${r.data?.updated || 0} güncel`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "İçe aktarım başarısız");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl border border-slate-200 w-full max-w-xl my-8" data-testid="creditor-import-modal">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold flex items-center gap-2">
            <Upload size={18} /> Toplu Müvekkil İçe Aktar
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg hover:bg-slate-100 grid place-items-center">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
            <div className="font-bold mb-1">Kolonlar (.xlsx veya .csv):</div>
            <ul className="list-disc list-inside space-y-0.5">
              <li><b>Ad</b> (zorunlu) · aynı isim varsa güncellenir</li>
              <li><b>Etiket</b> (opsiyonel) · vip / long_term / aggressive / new / watch</li>
              <li><b>Not</b> (opsiyonel) · max 1000 karakter</li>
              <li><b>Favori</b> (opsiyonel) · evet / 1 / true → <strong>mevcut favoriler değiştirilir</strong> (max 3)</li>
            </ul>
            <div className="mt-1.5 text-[11px] text-blue-800/80">
              CSV: UTF-8 encoding, delimiter otomatik tespit edilir (<code>, ; \t</code>)
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              data-testid="creditor-import-template-xlsx"
              onClick={() => downloadTemplate("xlsx")}
              type="button"
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-xs font-bold"
            >
              <Download size={13} /> Şablon .xlsx
            </button>
            <button
              data-testid="creditor-import-template-csv"
              onClick={() => downloadTemplate("csv")}
              type="button"
              className="inline-flex items-center gap-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-xs font-bold"
            >
              <Download size={13} /> Şablon .csv
            </button>
            <input
              data-testid="creditor-import-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); }}
              className="flex-1 min-w-[180px] text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-slate-200 file:bg-white file:text-slate-700 file:font-semibold file:cursor-pointer hover:file:bg-slate-50"
            />
          </div>
          {result && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs" data-testid="creditor-import-result">
              <div className="font-bold text-emerald-900 mb-1 flex items-center gap-1.5">
                Sonuç:
                {result.format && (
                  <span className="inline-flex items-center bg-emerald-200 text-emerald-900 rounded-full px-1.5 text-[9px] font-bold uppercase tracking-wider">
                    {result.format}
                  </span>
                )}
              </div>
              <ul className="space-y-0.5 text-emerald-800">
                <li>• Yeni kayıt: <strong>{result.created}</strong></li>
                <li>• Güncellenen: <strong>{result.updated}</strong></li>
                <li>• Favori olarak ayarlanan: <strong>{result.favorites_set}</strong>
                  {result.favorite_names?.length > 0 && (
                    <span className="opacity-80"> ({result.favorite_names.join(", ")})</span>
                  )}
                </li>
              </ul>
              {result.errors?.length > 0 && (
                <div className="mt-2 text-amber-800">
                  <strong>Uyarılar ({result.errors.length}):</strong>
                  <ul className="list-disc list-inside mt-1 space-y-0.5">
                    {result.errors.slice(0, 5).map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50"
          >
            {result ? "Kapat" : "İptal"}
          </button>
          {result ? (
            <button
              data-testid="creditor-import-finish"
              onClick={onDone}
              className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
            >
              Tamam, Listeyi Yenile
            </button>
          ) : (
            <button
              data-testid="creditor-import-submit"
              onClick={doImport}
              disabled={!file || busy}
              className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-50"
            >
              {busy ? "İçe aktarılıyor..." : "İçe Aktar"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
